import type { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';

import { RequireAuth } from './RequireAuth';
import { useAuth } from './session';

/**
 * Gate its children behind a specific role, on top of an authenticated
 * session.
 *
 * This is a *cosmetic* guard. The server's `requireRole` middleware is the
 * real authorization boundary; this guard exists only so a STANDARD user who
 * follows a stale link to an ADMIN page lands somewhere coherent instead of
 * staring at a broken-looking 403 page rendered inside the app shell.
 *
 * Composition (deliberate — anonymous/loading logic is NOT duplicated here):
 *   - Anonymous / loading is delegated entirely to `<RequireAuth>` by
 *     wrapping the role check inside it. So an anonymous visitor still ends
 *     at `/login` via exactly the same path as any other protected route,
 *     and the `loading` placeholder is the same one — there is one source of
 *     truth for "is there a session", and this component does not re-derive
 *     it.
 *   - Once `RequireAuth` has let us through, the session is authenticated, so
 *     `useAuth().user` is non-null. We compare its role to the required one:
 *       - role matches  → render `children`.
 *       - role mismatch → redirect to `/runs` (the universally-accessible
 *         landing route). `replace` so the back button does not bounce the
 *         user straight back into the page they are not allowed to see.
 *
 * Why redirect rather than render a "Not authorised" panel: arch §5.10 offers
 * either; we take the redirect so a STANDARD user is never parked on a dead
 * page and the FE never has to own an error surface that the API already owns
 * authoritatively.
 */
export interface RequireRoleProps {
  role: 'ADMIN' | 'STANDARD';
  children: ReactNode;
}

export function RequireRole({ role, children }: RequireRoleProps): JSX.Element {
  return (
    <RequireAuth>
      <RoleGate role={role}>{children}</RoleGate>
    </RequireAuth>
  );
}

/**
 * The role check itself. Only ever rendered by `RequireRole` *inside*
 * `RequireAuth`, so by the time it runs the session is authenticated and
 * `user` is non-null. We still guard the null case defensively — if `user`
 * were somehow null here it is a programming error in the composition, and a
 * redirect to `/runs` is a safe, non-disclosing fallback rather than a crash.
 */
function RoleGate({ role, children }: RequireRoleProps): JSX.Element {
  const { user } = useAuth();

  if (user === null || user.role !== role) {
    return <Navigate to="/runs" replace />;
  }

  return <>{children}</>;
}
